import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Tag, Spin } from 'antd';
import { AimOutlined } from '@ant-design/icons';

// 素质维度配置
const TRAIT_CONFIG = {
  mobility: {
    name: '活动度',
    icon: '🤸',
    color: '#52c41a',
    bg: '#f6ffed',
    border: '#b7eb8f',
  },
  strengthPerformance: {
    name: '力量',
    icon: '💪',
    color: '#fa8c16',
    bg: '#fff7e6',
    border: '#ffd591',
  },
  control: {
    name: '控制',
    icon: '🎯',
    color: '#1890ff',
    bg: '#e6f7ff',
    border: '#91d5ff',
  },
  stability: {
    name: '稳定',
    icon: '⚖️',
    color: '#722ed1',
    bg: '#f9f0ff',
    border: '#d3adf7',
  },
  coordination: {
    name: '协调',
    icon: '🔄',
    color: '#eb2f96',
    bg: '#fff0f6',
    border: '#ffadd2',
  },
  agility: {
    name: '敏捷',
    icon: '⚡',
    color: '#13c2c2',
    bg: '#e6fffb',
    border: '#87e8de',
  },
  powerPerformance: {
    name: '爆发',
    icon: '🚀',
    color: '#f5222d',
    bg: '#fff1f0',
    border: '#ffa39e',
  },
};

// 特征中文名映射
const FEATURE_LABELS = {
  jointRom: '关节活动范围',
  minAngle: '最小角度',
  maxAngle: '最大角度',
  targetSignalRange: '目标信号范围',
  normalizedMaxDisplacement: '归一化位移',
  eccentricDuration: '离心时长',
  concentricDuration: '向心时长',
  eccentricConcentricDurationRatio: '离/向时长比',
  concentricMinAngularVelocity: '向心最低角速度',
  phaseCompleteness: '阶段完整性',
  phaseConsistency: '阶段一致性',
  cycleDurationCv: '周期时长变异',
  cadence: '动作频率',
  bodyCenterSwayX: '重心横向摆动',
  bodyCenterSwayY: '重心纵向摆动',
  trunkTiltStd: '躯干倾角标准差',
  trunkTiltRange: '躯干倾角范围',
  leftRightTimingDiff: '左右时序差',
  phaseSync: '阶段同步性',
  peakAngularVelocity: '峰值角速度',
  peakAcceleration: '峰值加速度',
};

// 证据状态配置
const EVIDENCE_STATUS_CONFIG = {
  sufficient_evidence: { label: '充分证据', color: 'green' },
  insufficient_evidence: { label: '证据不足', color: 'orange' },
  no_mapping: { label: '无映射', color: 'default' },
};

const formatValue = (val, unit) => {
  if (val == null) return '--';
  if (typeof val === 'number') {
    if (unit === 'ms' && val >= 1000) return (val / 1000).toFixed(2) + 's';
    if (unit === 'ms') return val.toFixed(0) + 'ms';
    if (unit === '°' || unit === 'degree') return val.toFixed(1) + '°';
    if (unit === '%') return val.toFixed(1) + '%';
    if (unit === 'norm') return val.toFixed(3);
    if (unit === 'ratio') return val.toFixed(2);
    if (unit === 'count/min') return val.toFixed(1) + '次/分';
    if (unit === 'degree/s') return val.toFixed(1) + '°/s';
    if (unit === 'degree/s²') return val.toFixed(1) + '°/s²';
    return val.toFixed(2);
  }
  return String(val);
};

const TraitMappingCards = ({ layer4Url, onAvailabilityChange }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    if (!layer4Url) {
      setData(null);
      setLoading(false);
      onAvailabilityChange(false);
      return () => {
        active = false;
      };
    }
    setLoading(true);
    fetch(layer4Url)
      .then((res) => {
        if (!res.ok) throw new Error(`Layer 4 request failed: ${res.status}`);
        return res.json();
      })
      .then((json) => {
        if (!active) return;
        setData(json);
        setLoading(false);
        onAvailabilityChange(Boolean(json?.traitMappings?.length));
      })
      .catch(() => {
        if (!active) return;
        setData(null);
        setLoading(false);
        onAvailabilityChange(false);
      });
    return () => {
      active = false;
    };
  }, [layer4Url, onAvailabilityChange]);

  if (loading) {
    return (
      <div
        style={{
          textAlign: 'center',
          padding: 20,
          background: '#fafafa',
          borderRadius: 8,
        }}
      >
        <Spin size="small" />
      </div>
    );
  }

  if (!data || !data.traitMappings || data.traitMappings.length === 0)
    return null;

  const traitMappings = data.traitMappings;

  return (
    <div
      style={{
        background: '#fff',
        borderRadius: 8,
        padding: '14px 16px',
        marginBottom: 16,
        border: '1px solid #f0f0f0',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
        <AimOutlined style={{ color: '#722ed1', marginRight: 6 }} />
        <span style={{ fontWeight: 600, fontSize: 14 }}>运动特性概览</span>
        <Tag color="purple" style={{ marginLeft: 8 }}>
          {traitMappings.length} 个维度
        </Tag>
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: 12,
        }}
      >
        {traitMappings.map((trait, idx) => {
          const config = TRAIT_CONFIG[trait.traitCode] || {
            name: trait.traitName,
            icon: '📊',
            color: '#666',
            bg: '#fafafa',
            border: '#d9d9d9',
          };
          const statusConfig =
            EVIDENCE_STATUS_CONFIG[trait.evidenceStatus] ||
            EVIDENCE_STATUS_CONFIG.no_mapping;
          const evidence = trait.evidence || [];
          const problems = trait.problems || [];

          return (
            <div
              key={idx}
              style={{
                background: config.bg,
                borderRadius: 8,
                padding: '12px 14px',
                border: `1px solid ${config.border}`,
              }}
            >
              {/* 头部：图标 + 名称 + 状态 */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: 10,
                }}
              >
                <span style={{ fontSize: 20, marginRight: 8 }}>
                  {config.icon}
                </span>
                <span
                  style={{
                    fontWeight: 600,
                    fontSize: 14,
                    color: config.color,
                    flex: 1,
                  }}
                >
                  {config.name}
                </span>
                <Tag
                  color={statusConfig.color}
                  style={{ margin: 0, fontSize: 11 }}
                >
                  {statusConfig.label}
                </Tag>
              </div>

              {/* 证据列表 */}
              {evidence.length > 0 && (
                <div style={{ marginBottom: problems.length > 0 ? 10 : 0 }}>
                  <div style={{ fontSize: 11, color: '#666', marginBottom: 6 }}>
                    证据指标：
                  </div>
                  <div
                    style={{ display: 'flex', flexDirection: 'column', gap: 4 }}
                  >
                    {evidence.map((ev, evIdx) => {
                      const featureName =
                        ev.displayName ||
                        FEATURE_LABELS[ev.featureName] ||
                        ev.featureName ||
                        ev.featureRef;
                      return (
                        <div
                          key={evIdx}
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            fontSize: 12,
                            padding: '4px 8px',
                            background: 'rgba(255,255,255,0.7)',
                            borderRadius: 4,
                          }}
                        >
                          <span style={{ color: '#333' }}>{featureName}</span>
                          <span
                            style={{ fontWeight: 600, color: config.color }}
                          >
                            {formatValue(ev.value, ev.unit)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* 问题列表 */}
              {problems.length > 0 && (
                <div>
                  <div
                    style={{
                      fontSize: 11,
                      color: '#ff4d4f',
                      marginBottom: 6,
                      fontWeight: 600,
                    }}
                  >
                    ⚠️ 检测到问题 ({problems.length})
                  </div>
                  <div
                    style={{ display: 'flex', flexDirection: 'column', gap: 4 }}
                  >
                    {problems.map((prob, probIdx) => {
                      const featureName =
                        prob.displayName ||
                        FEATURE_LABELS[prob.featureName] ||
                        prob.featureName;
                      const severity = prob.severity || 'minor';
                      const severityColor =
                        severity === 'major' ? '#ff4d4f' : '#faad14';
                      return (
                        <div
                          key={probIdx}
                          style={{
                            fontSize: 11,
                            padding: '4px 8px',
                            background:
                              severity === 'major' ? '#fff2f0' : '#fffbe6',
                            border: `1px solid ${
                              severity === 'major' ? '#ffccc7' : '#ffe58f'
                            }`,
                            borderRadius: 4,
                            color: '#333',
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 6,
                              marginBottom: 2,
                            }}
                          >
                            <Tag
                              color={severityColor}
                              style={{
                                margin: 0,
                                fontSize: 10,
                                lineHeight: '16px',
                                padding: '0 4px',
                              }}
                            >
                              {severity === 'major' ? '严重' : '轻微'}
                            </Tag>
                            <span style={{ fontWeight: 500 }}>
                              {featureName}
                            </span>
                          </div>
                          {prob.description && (
                            <div
                              style={{
                                fontSize: 11,
                                color: '#666',
                                marginLeft: 2,
                              }}
                            >
                              {prob.description}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* 无证据提示 */}
              {evidence.length === 0 && (
                <div
                  style={{
                    fontSize: 11,
                    color: '#999',
                    textAlign: 'center',
                    padding: '8px 0',
                  }}
                >
                  暂无证据数据
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

TraitMappingCards.propTypes = {
  layer4Url: PropTypes.string,
  onAvailabilityChange: PropTypes.func,
};

TraitMappingCards.defaultProps = {
  layer4Url: '',
  onAvailabilityChange: () => {},
};

export default TraitMappingCards;
